/**
 * @whatItDoes This service performs login and logout function to authenticate against the server
 *
 * @howToUse Call login() with user credentials to make an HTTP call to the /login resource and
 * store the returned token if the login is successful
 */
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { UZURV_API_CONFIGURATION, UzurvApiConfiguration } from '@uzurv/rideWss/configurations/uzurv-api.configuration';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthenticationErrorConstants } from '../../constants';
import {
  AccountDisabledException,
  AccountExpiredException,
  AccountLockedException,
  AccountNotVerifiedException,
  BadCredentialsException,
  RoleNotFoundException
} from '../../exceptions';
import { AuthData } from '../../models';
import { AuthDataService } from '../auth-data';
import { UserService } from '../user';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  constructor(
    @Inject(UZURV_API_CONFIGURATION)
    protected uzurvApiConfiguration: UzurvApiConfiguration,
    private http: HttpClient,
    private authDataService: AuthDataService,
    private userService: UserService
  ) {}

  /**
   * Calls the authenticateUser() function to make the authentication HTTP call
   * and stores the authentication token using AuthDataService if the call is successful
   *
   * @param username the email address of the user
   * @param password the user's password
   */
  login(username: string, password: string): Subject<AuthData> {
    const subject = new Subject<AuthData>();

    this.authenticateUser(username, password).subscribe({
      next: (value: HttpResponse<object>) => {
        const authData = new AuthData(value.headers.get('x-auth-token'));
        this.authDataService.login(authData);

        // Need user permissions
        this.userService.listPermissionsForUser(authData.id).subscribe({
          next: permissions => {
            this.authDataService.setPermissions(permissions);

            subject.next(authData);
            subject.complete();

            this.authDataService.sendUserLoggedInEvent();
          },
          error: err => {
            subject.error(err);
            subject.complete();
          }
        });
      },
      error: err => {
        subject.error(err);
        subject.complete();
      }
    });

    return subject;
  }

  /**
   * Performs logout functions through the AuthDataService to clear local
   * authentication data
   */
  logout(): Observable<HttpResponse<object>> {
    const url: string = `${this.uzurvApiConfiguration.claraApiUrl}logout`;

    return this.http.post(url, null, { observe: 'response' });
  }

  /**
   * Sets up an HTTP call to the /login resource at the server
   *
   * First builds the URL with the configured rest base URL, then builds
   * a request body out of the authentication credentials.
   *
   * @param username
   * @param password
   *
   * @returns Observable<HttpResponse<Object>>
   *
   * TODO: replace object with more appropriate type.
   */
  private authenticateUser(username: string, password: string): Observable<HttpResponse<object>> {
    const url: string = `${this.uzurvApiConfiguration.claraApiUrl}login`;

    const body = new FormData();
    body.set('username', username.toLowerCase());
    body.set('password', password);

    return this.http.post(url, body, { observe: 'response' }).pipe(
      catchError(err => {
        if (AuthenticationErrorConstants.ACCOUNT_NOT_VERIFIED.test(err.message)) {
          return throwError(() => new AccountNotVerifiedException(err.message));
        } else if (AuthenticationErrorConstants.DISABLED.test(err.message)) {
          return throwError(() => new AccountDisabledException(err.message));
        } else if (AuthenticationErrorConstants.BAD_CREDENTIALS.test(err.message)) {
          return throwError(() => new BadCredentialsException(err.message));
        } else if (AuthenticationErrorConstants.LOCKED.test(err.message)) {
          return throwError(() => new AccountLockedException(err.message));
        } else if (AuthenticationErrorConstants.ROLE_NOT_FOUND.test(err.message)) {
          return throwError(() => new RoleNotFoundException(err.message));
        } else if (AuthenticationErrorConstants.ACCOUNT_EXPIRED.test(err.message)) {
          return throwError(() => new AccountExpiredException(err.message));
        }

        return throwError(() => err);
      })
    );
  }
}
